import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useCallback, useEffect, useState } from 'react';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useRegistersListQuery } from '@/modules/common/queries/registers.ts';
import RegisterProgressBar from '@/modules/admin-dashboard/components/RegisterProgressBar.tsx';
import { InfoCard, Skeleton } from 'ui';
import { CalendarGreyIcon, ChevronDownGreyIcon, ChevronDownIcon } from 'ui/icons';

dayjs.extend(quarterOfYear);

const emptyState = (
  <div className="h-full relative">
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[53px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[75px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[120px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[64px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[79px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[75px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[34px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[68px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[72px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[55px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="flex justify-between border-b py-4">
      <div className="bg-[#F5F5F5] w-[79px] h-[14px] rounded-lg"></div>
      <div className="bg-[#F5F5F5] w-[68px]  h-[14px] rounded-lg"></div>
    </div>
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      No data to display
    </div>
  </div>
);

const RegistersSkeleton = () => {
  return (
    <div className="flex flex-col gap-4 h-[300px]">
      <div className="flex flex-col gap-3">
        <Skeleton width={53} height={15} />
        <Skeleton height={4} />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton width={87} height={15} />
        <Skeleton height={4} />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton width={59} height={15} />
        <Skeleton height={4} />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton width={67} height={15} />
        <Skeleton height={4} />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton width={53} height={15} />
        <Skeleton height={4} />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton width={87} height={15} />
        <Skeleton height={4} />
      </div>
    </div>
  );
};

const Registers = () => {
  const businessId = useGetCurrentBusinessId();
  const [dateFilter, setDateFilter] = useState({
    quarter: dayjs().quarter(),
    year: new Date().getFullYear(),
  });

  useEffect(() => {
    setDateFilter({
      quarter: dayjs().quarter(),
      year: new Date().getFullYear(),
    });
  }, [businessId]);
  const { quarter, year } = dateFilter;

  const startOfQuarter = dayjs(new Date(year, quarter * 3 - 2, 1)).startOf('quarter');
  const endOfQuarter = dayjs(startOfQuarter).endOf('quarter');
  const fromDate = dayjs(startOfQuarter).format('YYYY-MM-DD');
  const toDate = dayjs(endOfQuarter).format('YYYY-MM-DD');

  const { data, isFetching, isLoading } = useRegistersListQuery({
    page: 1,
    page_size: 15,
    from_date: fromDate,
    to_date: toDate,
  });

  const isFirstQuarter = quarter === 1;
  const isLastQuarter = quarter === 4;
  const isCurrentYear = year === new Date().getFullYear();
  const isCurrentQuarter = quarter === dayjs().quarter();

  const isPossibleSetNextQuarter = !isCurrentQuarter || !isCurrentYear;

  const registers = (data?.pages || []).flatMap(({ data }) => data);

  const handlePreviousQuarter = useCallback(() => {
    if (isFirstQuarter) {
      setDateFilter(filters => ({
        ...filters,
        quarter: 4,
        year: filters.year - 1,
      }));
    } else {
      setDateFilter(filters => ({ ...filters, quarter: filters.quarter - 1 }));
    }
  }, [isFirstQuarter, setDateFilter]);

  const handleNextQuarter = useCallback(() => {
    if (!isPossibleSetNextQuarter) return;

    if (isLastQuarter) {
      setDateFilter(filters => ({
        ...filters,
        quarter: 1,
        year: filters.year + 1,
      }));
    } else {
      setDateFilter(filters => ({ ...filters, quarter: filters.quarter + 1 }));
    }
  }, [isLastQuarter, isPossibleSetNextQuarter, setDateFilter]);

  const dateFilterControl = (
    <div className="flex gap-6 flex-row">
      <div className="flex gap-1">
        <img alt="calendar" src={CalendarGreyIcon} />
        <div>
          {dayjs(startOfQuarter).format('DD-MM-YYYY')}
          {' - '}
          {dayjs(endOfQuarter).format('DD-MM-YYYY')}
        </div>
      </div>
      <div className="flex gap-4 flex-row">
        <img
          src={ChevronDownIcon}
          alt="back"
          className="cursor-pointer rotate-90 w-6"
          onClick={isFetching ? undefined : handlePreviousQuarter}
        />
        <img
          src={isPossibleSetNextQuarter ? ChevronDownIcon : ChevronDownGreyIcon}
          alt="forward"
          className="cursor-pointer -rotate-90  w-6"
          onClick={isFetching ? undefined : handleNextQuarter}
        />
      </div>
    </div>
  );
  const headerAction = isLoading ? (
    <div className="flex gap-3">
      <Skeleton width={15} height={15} borderRadius={15} />
      <Skeleton width={172} height={15} />
      <Skeleton width={15} height={15} borderRadius={15} />
      <Skeleton width={15} height={15} borderRadius={15} />
    </div>
  ) : (
    dateFilterControl
  );
  return (
    <InfoCard title="Compliance Progress" headerAction={headerAction} loading={isLoading}>
      {isFetching && <RegistersSkeleton />}
      {!isFetching && !registers.length && emptyState}
      {!isFetching && !!registers.length && (
        <div className="flex flex-col gap-3 h-[325px] overflow-auto">
          {registers.map(register => {
            const { id, name } = register;

            return (
              <div key={id} className="flex flex-col gap-2">
                <div className="text-neutral-500">{name}</div>
                <RegisterProgressBar register={register} fromDate={fromDate} toDate={toDate} />
              </div>
            );
          })}
        </div>
      )}
    </InfoCard>
  );
};

export default Registers;
